import { Search, Text, VSpacer } from '@/components/DesignSystem';
import { Table } from '@/components/DesignSystem/Table/Table';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { QueryKeys } from '@/constants/QueryKeys';
import { useHeightToViewportBottom } from '@/hooks/useHeightToViewportBottom';
import { useSearch } from '@/hooks/useSearch';
import { formatCell } from '@/pages/Admin/UserInspector/formatCell';
import { UserInspectorApi } from '@/utilities/api/UserInspectorApi';
import { UserAccountQueryEndpoint } from '@api/endpoints/UserAccountQueryEndpoint';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { QuerySortDirection } from '@shared/enums';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';
import { UserAccountFilters } from './UserAccountFilters';
import { orderUserAccountColumns } from './userAccountColumnOrder';
import { columnWidth, rowHeight, visibleRowRange } from './userAccountTableLayout';

type Column = UserAccountQueryEndpoint.Query.UserAccountQueryColumn;
type Filter = UserAccountQueryEndpoint.UserAccountQueryFilter;
type Response = UserAccountQueryEndpoint.Query.Response;
type Sort = UserAccountQueryEndpoint.UserAccountQuerySort;

const defaultSort: Sort = { column: 'createdAt', direction: QuerySortDirection.Desc };
const minTableHeight = 320;

// Columns have fixed widths, so a long name is clipped rather than spilling into the next one.
const headerCellSx = { overflow: 'hidden', whiteSpace: 'nowrap' };

// Always drawn, so the horizontal scrollbar shows even where the OS hides scrollbars when idle.
const visibleScrollbarsSx = {
  '&::-webkit-scrollbar': { height: '12px', width: '12px' },
  '&::-webkit-scrollbar-corner': { backgroundColor: 'grey.200' },
  '&::-webkit-scrollbar-thumb': {
    '&:hover': { backgroundColor: 'grey.600' },
    backgroundColor: 'grey.500',
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-track': { backgroundColor: 'grey.200' },
  scrollbarWidth: 'auto',
};

const rowCellSx = { height: rowHeight, overflow: 'hidden', py: 0 };

const ValueCell = ({ nullLabel, value }: { nullLabel: string, value: unknown }) => {
  if (value === null || value === undefined) {
    return <Text category="body-small" disabled fontStyle="italic">{nullLabel}</Text>;
  }
  const text = formatCell(value);
  return (
    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={text}>
      <Text category="body-small" fontSize={13} noWrap>{text}</Text>
    </Box>
  );
};

/** Stands in for the rows scrolled out of view, so the scrollbar keeps the whole list's length. */
const SpacerRow = ({ columnCount, height }: { columnCount: number, height: number }) => (
  height > 0 ? (
    <TableRow aria-hidden sx={{ height }}>
      <TableCell colSpan={columnCount} sx={{ border: 0, p: 0 }} />
    </TableRow>
  ) : null
);

export const UserAccounts = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filter[]>([]);
  const [sort, setSort] = useState<Sort>(defaultSort);
  const [lastLoadedColumns, setLastLoadedColumns] = useState<Column[]>([]);
  const { debouncedSearch, search, setSearch } = useSearch(1);
  const tableTopRef = useRef<HTMLDivElement>(null);
  const heightToViewportBottom = useHeightToViewportBottom(tableTopRef);
  const tableHeight = Math.max(heightToViewportBottom, minTableHeight);
  const [scrollTop, setScrollTop] = useState(0);
  // A new list starts at its top, so the table remounts, and scrolls back, for each one.
  const listKey = JSON.stringify([filters, debouncedSearch, sort]);
  useEffect(() => setScrollTop(0), [listKey]);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery<Response, Error>(
    [QueryKeys.QUERY_USER_ACCOUNTS, filters, debouncedSearch, sort],
    ({ pageParam }) => UserInspectorApi.queryUserAccounts({
      after: pageParam,
      filters,
      search: debouncedSearch,
      sort,
    }),
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      keepPreviousData: true,
      onSuccess: (result) => setLastLoadedColumns(result.pages[0].columns),
      retry: false,
    },
  );

  const rows = data?.pages.flatMap((page) => page.rows) ?? [];
  // Cached rows render before onSuccess runs, and a failed query has no columns of its own.
  const loadedColumns = data?.pages[0]?.columns ?? lastLoadedColumns;
  const columns = useMemo(() => orderUserAccountColumns(loadedColumns), [loadedColumns]);
  const nullLabel = t($ => $.userAccounts.null);
  // Only the rows in view are rendered; a few pages of every column would be tens of thousands
  // of cells.
  const { first, last } = visibleRowRange({
    rowCount: rows.length,
    scrollTop,
    viewportHeight: tableHeight,
  });

  const onSort = (column: string) => {
    setSort((previous) => ({
      column,
      direction: previous.column === column && previous.direction === QuerySortDirection.Asc
        ? QuerySortDirection.Desc
        : QuerySortDirection.Asc,
    }));
  };

  const headers = columns.map(({ name }) => (
    <TableSortLabel
      active={sort.column === name}
      data-testid={`user-accounts-sort-${name}`}
      direction={sort.column === name ? sort.direction : QuerySortDirection.Asc}
      key={name}
      onClick={() => onSort(name)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {name}
    </TableSortLabel>
  ));

  const rowCountLabel = hasNextPage
    ? t($ => $.userAccounts.rowCountWithMore, { count: rows.length })
    : t($ => $.userAccounts.rowCount, { count: rows.length });

  return (
    <Container maxWidth={false}>
      <VSpacer size="5" />
      <Stack alignItems="baseline" columnGap={2} direction="row" flexWrap="wrap">
        <Text category="title-large">{t($ => $.userAccounts.title)}</Text>
        <Text category="body-small" sx={{ color: 'text.secondary' }}>
          {t($ => $.userAccounts.description)}
        </Text>
      </Stack>
      <VSpacer size="3" />
      <UserAccountFilters
        columns={columns}
        onApply={setFilters}
        toolbarEnd={(
          <Stack alignItems="center" direction="row" spacing={1}>
            {isFetching && <CircularProgress size={20} />}
            {!error && (
              <Text category="body-medium" testID="user-accounts-row-count">
                {rowCountLabel}
              </Text>
            )}
          </Stack>
        )}
        toolbarStart={(
          <Search
            height={40}
            onChangeText={setSearch}
            placeholder={t($ => $.userAccounts.search)}
            testID="user-accounts-search"
            value={search}
            width={320}
          />
        )}
      />
      <VSpacer size="3" />
      {!!error && (
        <>
          <Alert data-testid="user-accounts-error" severity="error">{error.message}</Alert>
          <VSpacer size="4" />
        </>
      )}
      <div ref={tableTopRef} />
      <InfiniteScroll
        aria-busy={isFetching}
        data-testid="user-accounts-table"
        fetchMoreItems={() => (hasNextPage ? fetchNextPage() : Promise.resolve())}
        hasMore={hasNextPage ?? false}
        height={`${tableHeight}px`}
        isLoading={isFetching}
        key={listKey}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        sx={visibleScrollbarsSx}
      >
        <Table
          headerSx={headerCellSx}
          headerWidths={columns.map(columnWidth)}
          headers={headers}
          size="small"
          stickyHeader
        >
          <TableBody>
            <SpacerRow columnCount={columns.length} height={first * rowHeight} />
            {rows.slice(first, last).map((row) => (
              <TableRow key={String(row.id)}>
                {columns.map(({ name }) => (
                  <TableCell key={name} sx={rowCellSx}>
                    <ValueCell nullLabel={nullLabel} value={row[name]} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <SpacerRow columnCount={columns.length} height={(rows.length - last) * rowHeight} />
          </TableBody>
        </Table>
        {!isFetching && !error && rows.length === 0 && (
          <Box p={2}>
            <Text category="body-medium">{t($ => $.userAccounts.noRows)}</Text>
          </Box>
        )}
      </InfiniteScroll>
    </Container>
  );
};
